import {Link, useNavigate} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {Telescope} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {type Job} from '@/api/jobs';
import {jobStatusLabel, jobStatusTone, toneBadge} from '@/lib/status';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';

const COLUMN_COUNT = 5;

export function JobsTable({jobs}: {readonly jobs: Job[]}) {
  const navigate = useNavigate();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Search Term</TableHead>
          <TableHead>Strategy</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Items</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="p-0">
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Telescope />
                  </EmptyMedia>
                  <EmptyTitle>No jobs yet</EmptyTitle>
                  <EmptyDescription>No search has been started yet.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            </TableCell>
          </TableRow>
        ) : null}
        {jobs.map((job) => (
          <TableRow
            key={job.id}
            className="hover:bg-muted/50 cursor-pointer"
            onClick={async () => navigate(`/jobs/${job.id}`)}>
            <TableCell className="max-w-[240px] truncate font-medium">
              {/* The row's click is a pointer convenience; the link is the keyboard route, and it
                  stops the row from navigating a second time underneath it. */}
              <Button
                variant="link"
                size="sm"
                className="h-auto max-w-full p-0 font-medium"
                render={<Link to={`/jobs/${job.id}`} />}
                onClick={(event) => {
                  event.stopPropagation();
                }}>
                <span className="truncate">{job.search_term}</span>
              </Button>
            </TableCell>
            <TableCell className="text-muted-foreground">{job.strategy}</TableCell>
            <TableCell>
              <Badge {...toneBadge(jobStatusTone(job.status))}>{jobStatusLabel(job.status)}</Badge>
            </TableCell>
            <TableCell className="text-right">{job.item_count.toLocaleString()}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatDistanceToNow(new Date(job.created_at), {addSuffix: true})}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
